import * as d3 from "d3";
import _ from "lodash";
import React, { useContext, useEffect, useState } from "react";
import { Button, Segment, Table } from "semantic-ui-react";
import { LimitData, Measurement, MeasurementData, VectorData } from "../../models/testing_model";
import { TestResultMissingPointsContext } from "../../context";

function retrieveRawLimits(result: VectorData, rawLimits: Map<number, { x: number, y: number }>, limitValue?: { x_values: number[], y_values: number[] }) {
    if (limitValue) {
        (limitValue.x_values as number[]).forEach((x, limitIndex) => {
            let findIndex = -1;
            (result.x_values || []).find((rx, i) => {
                if (x <= rx) {
                    findIndex = i;
                    return true;
                }
            });

            if (findIndex !== -1) {
                if (findIndex !== 0 && Math.abs(result.x_values[findIndex] - x) > Math.abs(result.x_values[findIndex - 1] - x)) {
                    findIndex -= 1;
                }

                rawLimits.set(result.x_values[findIndex], { x, y: limitValue!.y_values[limitIndex] })
            }
        });
    }
}

export function VectorDataComponent(props: {
    measurement: Measurement,
    data: MeasurementData[]
}) {
    const { measurement, data: allData } = props;

    const [data, setData] = useState<MeasurementData[]>(allData);

    const channels = _.uniq(allData.map(d => d.channel)).sort();
    const previousIndexes = _.uniq(allData.map(d => d.index)).sort();

    const currentAttempts = useState(_.max(previousIndexes));
    const sHtmlContent = useState<any>(<></>);

    const sIsMissingPoints = useContext(TestResultMissingPointsContext);

    useEffect(() => {
        setData(allData.filter(d => d.index === currentAttempts[0]));
    }, [allData, currentAttempts[0]]);

    useEffect(() => {
        let hasLimitError = false;
        sHtmlContent[1](channels.map((channel, index) => {
            const channelData = data[index];

            const rawLowerLimits: Map<number, { x: number, y: number }> = new Map();
            const rawUpperLimits: Map<number, { x: number, y: number }> = new Map();

            const limits = channelData.limits as LimitData<VectorData>;
            const result = channelData.result as VectorData;

            let lowerScale: (x: number) => any;
            if (limits.lower) {
                const scale = d3.scaleLinear().domain(limits.lower.x_values).range(limits.lower.y_values);
                const minX = _.first(limits.lower.x_values)!;
                const maxX = _.last(limits.lower.x_values)!;
                lowerScale = (x: number) => {
                    if (x >= minX && x <= maxX) {
                        return scale(x);
                    }

                    return null;
                }
            } else {
                lowerScale = (x: number) => null
            }
            let upperScale: (x: number) => any;
            if (limits.upper) {
                const scale = d3.scaleLinear().domain(limits.upper.x_values).range(limits.upper.y_values);
                const minX = _.first(limits.upper.x_values)!;
                const maxX = _.last(limits.upper.x_values)!;
                upperScale = (x: number) => {
                    if (x >= minX && x <= maxX) {
                        return scale(x);
                    }

                    return null;
                }
            } else {
                upperScale = (x: number) => null
            }
            if (limits) {
                retrieveRawLimits(result, rawLowerLimits, limits.lower);
                retrieveRawLimits(result, rawUpperLimits, limits.upper);
            }

            return <Segment key={channel}><Table selectable size="small" celled collapsing>
                <caption style={{ paddingBottom: '1rem' }}><h3>Channel{channel}</h3></caption>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>X</Table.HeaderCell>
                        <Table.HeaderCell>Lower{measurement.limit_unit ? ` (${measurement.limit_unit})` : ''}</Table.HeaderCell>
                        <Table.HeaderCell>Value{measurement.result_unit ? ` (${measurement.result_unit})` : ''}</Table.HeaderCell>
                        <Table.HeaderCell>Upper{measurement.limit_unit ? ` (${measurement.limit_unit})` : ''}</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {(result.x_values || []).map((x, i) => {
                        const y = result.y_values[i];
                        const interpolateLower = lowerScale(x);
                        const interploateUpper = upperScale(x);

                        let lowerStatus = ''
                        let upperStatus = ''
                        let valueStatus = 'positive';
                        if (y !== null && !_.isNumber(y)) {
                            valueStatus = 'error';
                        }

                        if (interpolateLower !== null && y < interpolateLower) {
                            valueStatus = 'error';
                            lowerStatus = 'error'

                            hasLimitError = true;
                        }

                        if (interploateUpper !== null && y > interploateUpper) {
                            valueStatus = 'error';
                            upperStatus = 'error'

                             hasLimitError = true;
                        }

                        let lower;
                        const rawlowerLimit = rawLowerLimits.get(x);
                        const rawUpperLimit = rawUpperLimits.get(x);
                        if (rawlowerLimit) {
                            if (rawlowerLimit.x !== x) {
                                lower = <b style={{ fontStyle: 'italic' }} title={`x: ${rawlowerLimit.x}, y: ${rawlowerLimit.y}`}>{interpolateLower || rawlowerLimit.y}</b>;
                            } else {
                                lower = <b>{interpolateLower || rawlowerLimit.y}</b>;
                            }
                        } else {
                            lower = <i style={{ opacity: 0.618 }}>{interpolateLower}</i>;
                        }

                        let upper;
                        if (rawUpperLimit) {
                            if (rawUpperLimit.x !== x) {
                                upper = <b style={{ fontStyle: 'italic' }} title={`x: ${rawUpperLimit.x}, y: ${rawUpperLimit.y}`}>{interploateUpper || rawUpperLimit.y}</b>;
                            } else {
                                upper = <b>{interploateUpper || rawUpperLimit.y}</b>;
                            }
                        } else {
                            upper = <i style={{ opacity: 0.618 }}>{interploateUpper}</i>;
                        }

                        return <Table.Row key={x}>
                            <Table.Cell>{x}</Table.Cell>
                            <Table.Cell className={lowerStatus}>{lower}</Table.Cell>
                            <Table.Cell className={valueStatus}>{y}</Table.Cell>
                            <Table.Cell className={upperStatus}>{upper}</Table.Cell>
                        </Table.Row>
                    })}

                </Table.Body>
            </Table></Segment>
        }));

        sIsMissingPoints[1](!hasLimitError && measurement.measurement_status == 2);
    }, [channels.join(','), data, measurement])

    return <>
        {previousIndexes.length > 1 && <>
            <Button.Group size="mini" compact>
                {previousIndexes.map(i => <Button key={i} size="mini" basic={currentAttempts[0] !== i} onClick={e => currentAttempts[1](i)}>Attempts {i}</Button>)}
            </Button.Group>
        </>}

        <Segment.Group horizontal>
            {sHtmlContent[0]}
        </Segment.Group>
    </>
}